import {
  type OutletResource,
  OutletsController_list_v1,
  TaxIdentityController_list_v1,
  type TaxIdentityResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import { Switch } from '@repo/ui/components/switch';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconCircleCheck, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { EditOutletDialog } from './EditOutletDialog';
import { NewOutletDialog } from './NewOutletDialog';
import { isRetired, labelForKind, labelForStock } from './outlet-kinds';
import { labelForTaxIdentity } from './registrations';

/**
 * The first screen of park setup: every outlet the park has, and the way to add
 * one.
 *
 * **An outlet is a place, never an account.** Nobody signs in as an outlet;
 * people are assigned to one (FE011) and terminals stand in one. Nothing on this
 * screen offers a credential, a password or a sign-in, and that absence is the
 * design rather than an omission — the moment a setup screen grows a "password"
 * field beside a place, somebody starts sharing one.
 *
 * **`code` is unique among outlets that have not been retired**, and a retired
 * outlet's code becomes available again. That is deliberate on the API's side —
 * unlike staff codes and terminal prefixes, which are never reused because
 * receipts carry them — so the 409 this screen handles means "a *live* outlet
 * holds it", and the copy `error-messages.ts` supplies says so and names
 * retiring as the way out.
 *
 * **The name is the handle.** A row carries no buttons of its own — the screen
 * has one accent and it is on "New outlet" — so an outlet is opened the way a
 * record is opened anywhere, by its name. That holds for a retired one too:
 * `EditOutletDialog` opens it read-only, because the whole point of a soft
 * delete is that somebody can still go and look.
 *
 * **Ten outlets today; fifty must need no code change.** The list is read whole
 * through `collectPages`, so the API's fifty-row default page does not quietly
 * become this screen's ceiling. The failure that avoids is not a visible one: a
 * list that stops at fifty looks complete, and the outlet that is missing gets
 * created a second time.
 */

/** Every state the list can be in. One value, so two of them cannot coexist. */
type ListState =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly outlets: readonly OutletResource[];
      /** The GST registrations, read beside the list so each row can name its own. */
      readonly taxIdentities: readonly TaxIdentityResource[];
    }
  | { readonly kind: 'failed'; readonly error: unknown };

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS_WITHOUT_STATUS = 5;

export function Outlets() {
  const client = useApiClient();

  const [includeRetired, setIncludeRetired] = useState(false);
  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<OutletResource | null>(null);

  // What just happened, said once above the table. One slot rather than three
  // booleans: creating, renaming and retiring are the same announcement in
  // different words, and two of them can never be true of the same moment.
  const [notice, setNotice] = useState<{ code: string; text: string } | null>(null);

  // Bumping this re-runs the load. A counter rather than a boolean, so two
  // retries in a row are two distinct values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const reload = useCallback(() => setReloads((count) => count + 1), []);

  useEffect(() => {
    // Two guards, not one. `controller` stops the request itself the moment the
    // toggle moves — there is no reason to leave it running — and `live` stops a
    // response that was already in flight from being written into state after
    // this effect was torn down. Without the second, flipping the toggle twice
    // quickly can land the first answer on top of the second and leave the
    // screen showing retired outlets with the switch off.
    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    Promise.all([
      collectPages<OutletResource>((offset, limit) =>
        client.request(OutletsController_list_v1, {
          // `includeRetired` is sent only when it is true. The API excludes
          // retired outlets by default (FR-112) and saying `false` explicitly
          // would work — but the query string is what appears in a log when
          // somebody is working out what a screen asked for, and a screen that
          // only ever asks for the default is more honest read that way.
          query: { offset, limit, ...(includeRetired ? { includeRetired: true } : {}) },
          signal: controller.signal,
        }),
      ),
      // Unpaged at the API: two registrations today, bounded by the park.
      client.request(TaxIdentityController_list_v1, { signal: controller.signal }),
    ])
      .then(([outlets, taxIdentities]) => {
        if (live) setState({ kind: 'loaded', outlets, taxIdentities });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, includeRetired, reloads]);

  // The status column earns its place only when it can discriminate. With
  // retired outlets hidden every row would read "Trading", which is a column of
  // one repeated word and a column staff learn to stop reading.
  const columns = includeRetired ? COLUMNS_WITHOUT_STATUS + 1 : COLUMNS_WITHOUT_STATUS;

  return (
    <Screen
      title="Outlets"
      description="Every place in the park that takes money — the gates, the shops and the activity centres. An outlet is a place, not an account; nobody signs in as one."
      action={
        // The screen's one primary action, and the only accent on it. Rows
        // carry no buttons of their own for the same reason.
        <Button onClick={() => setCreating(true)}>
          <IconPlus />
          New outlet
        </Button>
      }
      aside={
        <>
          {state.kind === 'loaded' && (
            <ScreenFact label="Showing">
              <span className="font-mono numeric">{state.outlets.length}</span>{' '}
              {state.outlets.length === 1 ? 'outlet' : 'outlets'}
              {includeRetired && ', retired included'}
            </ScreenFact>
          )}

          <div className="flex items-center gap-3">
            <Switch
              id="show-retired"
              aria-labelledby="show-retired-label"
              checked={includeRetired}
              onCheckedChange={(checked) => setIncludeRetired(checked)}
            />
            <Label
              id="show-retired-label"
              htmlFor="show-retired"
              className="cursor-pointer text-sm"
            >
              Show retired outlets
            </Label>
          </div>
        </>
      }
    >
      {notice !== null && (
        <Alert
          // `status` and not the component's default `alert`: a success is not
          // an interruption, and this is announced politely rather than cutting
          // across whatever the reader was doing.
          role="status"
        >
          <IconCircleCheck />
          <AlertDescription>
            <span className="font-mono text-foreground numeric">{notice.code}</span> — {notice.text}
          </AlertDescription>
        </Alert>
      )}

      {/* `columns="stated"`, so Name is 20rem and the slack falls at the end of
          the row rather than swelling the column in the middle of it.

          **Said here rather than solved once for every screen, and that is the
          finding rather than a shortcut.** FE066 built two rules that would have
          settled this for all twenty and rendered both against Outlets,
          Movements, Cash book and Profit and loss; both were cut, and "Open" in
          `docs/design-language.md` records what they were and what they broke.
          The short version is that a table ending in an amount needs its last
          column at the sheet's edge, where the figures right-align, and a table
          ending in text does not — and nothing in `ScreenTable` can tell which
          it is holding. Outlets ends in text, so Outlets says so.

          **A width on Name does not hold on its own**, which was the first thing
          tried: under automatic layout the leftover is shared back out across
          every column in proportion, so `w-80` still rendered Name at about
          415px and widened Kind and Stock with it. `w-full` on the last column
          was the second, and it is greedy — it takes everything above the other
          columns' minimums, which collapsed all four to their content. Fixed
          layout is what makes a stated width mean the width it states. */}
      <ScreenTable label="Outlets" columns="stated">
        <TableHeader>
          <TableRow>
            <TableHead className="w-32">Code</TableHead>
            {/* `w-80` is 320px, against about 150px for "Orchidarium Counter" —
                the longest name in the park today — so there is room for a name
                half as long again before anything gives. When one finally does,
                it wraps inside its own column instead of pushing the columns
                after it around, which is what a ruled sheet should do: the
                rulings stay put and the entry gives. */}
            <TableHead className="w-80">Name</TableHead>
            <TableHead className="w-40">Kind</TableHead>
            {/* **Every column states a width now, and that is a reversal.**
                It was the last pair that stated none, so under `stated` they
                shared what was left — which was right while the field was
                capped at `68rem` and wrong the moment FE068 gave the sheet the
                whole window. At 2000px the leftover is about a thousand pixels
                and it all landed in Stock, so "Holds stock" sat at the start of
                a column the width of the rest of the table put together.

                `docs/design-language.md`, under **Open**, cut "state them all"
                for this screen because the leftover is shared back out. That is
                exactly what is wanted at this width: shared out in proportion,
                the biggest share falls to `w-80` Name, which is the one column
                that can use it. The rule it was cut against — a width on Name
                alone does nothing — was measured under automatic layout, and
                fixed layout is what makes these numbers mean what they say. */}
            <TableHead className="w-44">Stock</TableHead>
            {/* Seen at a glance, because a wrong mapping prints the wrong GSTIN
                on every invoice the outlet issues (#321). */}
            <TableHead className="w-48">GST registration</TableHead>
            {includeRetired && <TableHead className="w-32">Status</TableHead>}
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={columns} />}

        {state.kind === 'failed' && (
          <TableNotice columns={columns}>
            <LoadFailed error={state.error} onRetry={reload} />
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (state.outlets.length === 0 ? (
            <TableNotice columns={columns}>
              <EmptyState
                title="No outlets yet"
                description={
                  includeRetired
                    ? 'Nothing has been set up here, retired or otherwise. Gates, shops and activity centres are all outlets, and New outlet creates the first one.'
                    : 'Gates, shops and activity centres are all outlets, and New outlet creates the first one. If some were set up and later retired, turn on Show retired outlets.'
                }
              />
            </TableNotice>
          ) : (
            <TableBody>
              {state.outlets.map((outlet) => (
                <OutletRow
                  key={outlet.id}
                  outlet={outlet}
                  taxIdentities={state.taxIdentities}
                  showStatus={includeRetired}
                  onOpen={() => setEditing(outlet)}
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      <NewOutletDialog
        open={creating}
        taxIdentities={state.kind === 'loaded' ? state.taxIdentities : []}
        onOpenChange={setCreating}
        onCreated={(outlet) => {
          setCreating(false);
          setNotice({ code: outlet.code, text: `${outlet.name} has been created.` });

          // Re-read rather than splicing the new outlet into the list. The API
          // decides the order and whether anything else moved while this form
          // was open, and a locally patched list is a second answer to a
          // question the server has already answered.
          reload();
        }}
      />

      {/* Mounted only while one is open, so the next outlet opened starts from
          its own values rather than from the last one's. */}
      {editing !== null && (
        <EditOutletDialog
          outlet={editing}
          taxIdentities={state.kind === 'loaded' ? state.taxIdentities : []}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          onUpdated={(outlet) => {
            setEditing(null);
            setNotice({ code: outlet.code, text: `${outlet.name} has been updated.` });
            reload();
          }}
          onRetired={(outlet) => {
            setEditing(null);
            setNotice({
              code: outlet.code,
              text: `${outlet.name} has been retired. Its history stays reachable under Show retired outlets, and its code is free for a new outlet.`,
            });

            // Re-read rather than dropping the row locally. With the toggle off
            // it disappears, with it on it comes back marked — and which of
            // those is right is a question the API has just been asked.
            reload();
          }}
        />
      )}
    </Screen>
  );
}

function OutletRow({
  outlet,
  taxIdentities,
  showStatus,
  onOpen,
}: {
  outlet: OutletResource;
  taxIdentities: readonly TaxIdentityResource[];
  showStatus: boolean;
  onOpen: () => void;
}) {
  const retired = isRetired(outlet);
  const registration = taxIdentities.find((identity) => identity.id === outlet.taxIdentityId);

  return (
    // A retired row is quieter, not struck through or greyed to the point of
    // being unreadable: it is history that must stay legible, because the whole
    // reason for keeping it reachable is that somebody is looking something up.
    // `text-muted-foreground` is safe here — the row sits on `bg-card`, and it
    // is only the muted-on-muted pairing that misses AA.
    <TableRow className={retired ? 'text-muted-foreground' : undefined}>
      {/* A code is read aloud and typed back in, so it gets the mono face and
          tabular figures — the column has to compare cleanly down its length. */}
      <TableCell className="font-mono text-foreground numeric">{outlet.code}</TableCell>
      <TableCell>
        {/* The row's only control, and it carries no chrome: the accent on this
            screen belongs to "New outlet". Underlined on hover and focus so it
            is discoverable as a control without twenty of them competing with
            the one primary action. */}
        <button
          type="button"
          className="rounded-sm text-left underline-offset-4 outline-none hover:underline focus-visible:underline focus-visible:ring-3 focus-visible:ring-ring/30"
          onClick={onOpen}
        >
          <span className={retired ? undefined : 'text-foreground'}>{outlet.name}</span>
        </button>
      </TableCell>
      <TableCell>{labelForKind(outlet.kind)}</TableCell>
      <TableCell>{labelForStock(outlet.carriesStock)}</TableCell>
      <TableCell>
        {/* Null only on an outlet made before registrations existed; its tills
            are refused an invoice header until one is chosen. */}
        {registration === undefined ? (
          <span className="text-muted-foreground">None</span>
        ) : (
          labelForTaxIdentity(registration)
        )}
      </TableCell>

      {showStatus && (
        <TableCell>
          {retired ? (
            // `outline` and not `destructive`. Retiring an outlet is an
            // ordinary end to an ordinary life, and red is reserved for a
            // system that refused something or is about to.
            <Badge variant="outline">Retired</Badge>
          ) : (
            <span className="text-muted-foreground">Trading</span>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
